<template>
  <div class="verify-result-container">
    <div class="summary">
      <h4 class="section-title">
        Summary
      </h4>
      <div class="section-content">
        <div class="summary-info">
          <p class="summary-info__item">
            Webpage URL:
            <span class="text-bold">
              {{ summary.webpageUrl }}
            </span>
          </p>
          <p class="summary-info__item">
            Verification date/time:
            <span class="text-bold">
              <time>
                {{ summary.verifyAt | dateString }}
              </time>
              <time>
                {{ summary.verifyAt | timeString }}
              </time>
            </span>
          </p>
          <p class="summary-info__item">
            URL status:
            <a-tag :color="STATUS_COLOR[summary.status]">
              {{ summary.status }}
            </a-tag>
            <template v-if="summary.newStatus">
              <a-icon type="arrow-right" />&nbsp;
              <a-tag :color="STATUS_COLOR[summary.newStatus]">
                {{ summary.newStatus }}
              </a-tag>
            </template>
          </p>
          <p class="summary-info__item">
            Incident ID:
            <span class="text-bold">
              {{ summary.incidentId }}
            </span>
          </p>
          <p class="summary-info__item mb-0">
            Summary:
            <span class="text-bold">
              {{ summary.detected }}
            </span>
          </p>
        </div>
        <div v-if="summary.baselineImage || summary.diffImage || summary.incidentImage" class="summary-image">
          <div>
            <p class="summary-image__title mr-2">
              Webpage Screenshot:
            </p>
            <a-radio-group :value="typeImg" @change="handleTypeImageChange">
              <a-radio-button v-if="summary.baselineImage" value="baseline">
                Baseline
              </a-radio-button>
              <a-radio-button v-if="summary.diffImage" value="difference">
                Difference
              </a-radio-button>
              <a-radio-button v-if="summary.incidentImage" value="incident">
                Last incident
              </a-radio-button>
            </a-radio-group>
            <p class="summary-image__title ml-2">
              <template v-if="(detectBaselineAt && typeImg === 'baseline') || (detectLastIncidentAt && typeImg === 'incident')">
                at
                <time v-show="typeImg === 'baseline'">
                  {{ detectBaselineAt | dateString }} {{ detectBaselineAt | timeString }}
                </time>
                <time v-show="typeImg === 'incident'">
                  {{ detectLastIncidentAt | dateString }} {{ detectLastIncidentAt | timeString }}
                </time>
              </template>
            </p>
          </div>
          <div class="img-container">
            <img v-show="typeImg === 'baseline'" :src="summary.baselineImage" alt="Webpage screenshot">
            <img v-show="typeImg === 'difference'" :src="summary.diffImage" alt="Webpage screenshot">
            <img v-show="typeImg === 'incident'" :src="summary.incidentImage" alt="Webpage screenshot">
          </div>
        </div>
      </div>
    </div>
    <div v-if="isHasLastIncidentData || isHasBaselineData" class="more-detail">
      <h4 class="section-title">
        More detail
      </h4>
      <div class="section-content">
        <div v-if="isHasLastIncidentData" class="changes-detect-section">
          <p class="changes-detect-section__title">
            Changes detected (from last alert<template v-if="detectLastIncidentAt">
              on
              <time>{{ detectLastIncidentAt | dateString }} {{ detectLastIncidentAt | timeString }}</time>
            </template>)
          </p>
          <VerifyResultTable
            :changes-detected="result.changes_detected_from_last_incident"
            comparison-type="previous"
          />
        </div>
        <div v-if="isHasBaselineData" class="changes-detect-section">
          <p class="changes-detect-section__title">
            Changes detected (from baseline
            <template v-if="detectBaselineAt">
              taken at
              <time>{{ detectBaselineAt | dateString }} {{ detectBaselineAt | timeString }}</time>
            </template>)
          </p>
          <VerifyResultTable
            :changes-detected="result.changes_detected_from_baseline"
            comparison-type="baseline"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { STATUS_COLOR } from '@constants/statusColor';
  import VerifyResultTable from './VerifyResultTable';

  export default {
    name: 'VerifyResult',
    components: {
      VerifyResultTable
    },
    props: {
      result: {
        type: Object,
        default: () => {}
      }
    },
    data() {
      return {
        STATUS_COLOR,
        summary: {
          webpageUrl: '',
          verifyAt: '',
          status: '',
          newStatus: '',
          incidentId: '',
          detected: '',
          captureAt: '',
          baselineImage: '',
          incidentImage: '',
          diffImage: ''
        },
        typeImg: 'incident',
        detectLastIncidentAt: '',
        detectBaselineAt: '',
      };
    },
    computed: {
      isHasLastIncidentData() {
        return this.result.changes_detected_from_last_incident;
      },
      isHasBaselineData() {
        return this.result.changes_detected_from_baseline;
      },
    },
    mounted() {
      this.summary = {
        webpageUrl: this.result.url || '--/--',
        verifyAt: this.result.start_time,
        status: this.result.url_status || '',
        newStatus: this.result.new_url_status || '',
        incidentId: this.result.incident_id || '--/--',
        detected: this.result.summary || '--/--',
        captureAt: this.result.screenshot_at,
        incidentImage: (this.result.screenshot && this.result.screenshot.after) || '',
        baselineImage: (this.result.screenshot && this.result.screenshot.before) || '',
        diffImage: (this.result.screenshot && this.result.screenshot.difference) || ''
      };

      this.detectLastIncidentAt = this.result.last_incident && this.result.last_incident.detected_time;
      this.detectBaselineAt = this.result.baseline && this.result.baseline.start_time;
    },
    methods: {
      handleTypeImageChange(e) {
        this.typeImg = e.target.value;
      },
    }
  };
</script>

<style lang="scss" scoped>
  .ant-input[disabled] {
    color: inherit;
    background-color: #ffffff;
  }
  .verify-result-container {
    .section-title {
      color: #05c2d1;
      font-family: Roboto;
      font-size: 18px;
      font-weight: 500;
      letter-spacing: 0;
      line-height: 24px;
      margin-bottom: 16px;
    }
    .changes-detect-section:not(:first-child) {
      margin-top: 30px;
      margin-bottom: 30px;
    }
    .summary {
      &-info {
        padding: 20px;
        margin-bottom: 20px;
        border-radius: 4px;
        background-color: #f7f7f7;
        &__item {
          color: #323232;
          font-family: Roboto;
          font-size: 13px;
          letter-spacing: 0;
          line-height: 20px;
        }
        .text-bold {
          font-weight: 500;
        }
      }
      &-image {
        margin-bottom: 20px;
        &__title {
          display: inline-block;
          color: #323232;
          font-family: Roboto;
          font-size: 13px;
          letter-spacing: 0;
          line-height: 20px;
        }
      }
    }
    .more-detail {
      margin-top: 20px;
    }
    .img-container {
      overflow-x: hidden;
      overflow-y: auto;
      height: auto;
      max-height: 450px;
    }
  }
</style>
